import { Action, NgxsOnInit, State, StateContext } from '@ngxs/store';
import {
   ChangeAudioInputDeviceId,
   ChangeAudioOutputDeviceId,
   ChangeRingerDeviceId,
   PrepareLocalStorageSettingsForModification,
   RejectChangedLocalStorageSettings,
   ResetDraftSettings,
   SaveLocalStorageSettings,
   ChangeHeadsetManufacturer,
} from './audio-devices-settings.actions';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Injectable } from '@angular/core';
import _ from 'lodash';
import { DEVICE_ID_ALL, DEVICE_ID_COMMUNICATIONS } from '../../../services/audio/audio.constancts';
import { HeadsetManufacturerType } from 'src/app/services/headset/headset-manufacturer-type';

export interface AudioSettings {
   ringerDeviceId: string;
   outputDeviceId: string;
   inputDeviceId: string;
   headsetManufacturerId: string;
}

export interface AudioDevicesSettingsStateModel {
   currentSettings: AudioSettings;
   draftSettings: AudioSettings;
}

export class DefaultAudioSettings {
   public static default: AudioSettings = {
      ringerDeviceId: DEVICE_ID_ALL,
      outputDeviceId: DEVICE_ID_COMMUNICATIONS,
      inputDeviceId: DEVICE_ID_COMMUNICATIONS,
      headsetManufacturerId: HeadsetManufacturerType.ALL.value,
   };
}

@State<AudioDevicesSettingsStateModel>({
   name: 'audioDevices',
   defaults: {
      draftSettings: null,
      currentSettings: DefaultAudioSettings.default,
   },
})
@Injectable()
export class AudioDevicesSettingsState implements NgxsOnInit {
   ngxsOnInit(ctx?: StateContext<AudioDevicesSettingsStateModel>) {
      // When the page is reloaded while the settings menu is open, draftSettings would be restored from localstorage.
      // Therefore reset draftSettings after initialization.
      ctx.dispatch(new ResetDraftSettings());
   }

   @Action(ResetDraftSettings)
   public resetDraftSettings(ctx: StateContext<AudioDevicesSettingsStateModel>, action: ResetDraftSettings) {
      ctx.patchState({
         draftSettings: null,
      });
   }

   @Action(PrepareLocalStorageSettingsForModification)
   public prepareForModification(ctx: StateContext<AudioDevicesSettingsStateModel>, action: PrepareLocalStorageSettingsForModification) {
      let currentSettings = _.cloneDeep(ctx.getState().currentSettings);
      // Check if setting is null, to prevent the user from being stuck in a state where the settings can't be set anymore.
      if (_.isNil(currentSettings)) {
         currentSettings = DefaultAudioSettings.default;
      }
      ctx.patchState({
         draftSettings: currentSettings,
      });
   }

   @Action(RejectChangedLocalStorageSettings)
   public rejectChangedSettings(ctx: StateContext<AudioDevicesSettingsStateModel>, action: RejectChangedLocalStorageSettings) {
      ctx.patchState({
         draftSettings: null,
      });
   }

   @Action(SaveLocalStorageSettings)
   public saveSettings(ctx: StateContext<AudioDevicesSettingsStateModel>, action: SaveLocalStorageSettings) {
      ctx.patchState({
         currentSettings: ctx.getState().draftSettings,
         draftSettings: null,
      });
   }

   @Action(ChangeRingerDeviceId)
   @ImmutableContext()
   public changeRingerDeviceId(ctx: StateContext<AudioDevicesSettingsStateModel>, action: ChangeRingerDeviceId) {
      ctx.setState((state) => {
         state.draftSettings.ringerDeviceId = action.deviceId;
         return state;
      });
   }

   @Action(ChangeAudioOutputDeviceId)
   @ImmutableContext()
   public changeAudioOutputDeviceId(ctx: StateContext<AudioDevicesSettingsStateModel>, action: ChangeAudioOutputDeviceId) {
      ctx.setState((state) => {
         state.draftSettings.outputDeviceId = action.deviceId;
         return state;
      });
   }

   @Action(ChangeAudioInputDeviceId)
   @ImmutableContext()
   public changeAudioInputDeviceId(ctx: StateContext<AudioDevicesSettingsStateModel>, action: ChangeAudioInputDeviceId) {
      ctx.setState((state) => {
         state.draftSettings.inputDeviceId = action.deviceId;
         return state;
      });
   }

   @Action(ChangeHeadsetManufacturer)
   @ImmutableContext()
   public changeHeadsetManufacturer(ctx: StateContext<AudioDevicesSettingsStateModel>, action: ChangeHeadsetManufacturer) {
      ctx.setState((state) => {
         state.draftSettings.headsetManufacturerId = action.manufacturer.value;
         return state;
      });
   }
}
